import { ConfigurationWithMandatoryBuildabilityV2 } from '@volkswagen-onehub/car-configuration-feature-service';
import React, { useEffect, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { dpsSend, getPriceDataWithMarketingCode } from 'src/bff';
import { getEquipmentlineName, getFeatures, getWheels } from 'src/bff/get-marketing-code';
import {
  FormController,
  removeWhiteSpace,
  useFaContent,
  useFeatureAppConfig,
  useGetInstalaciones,
  useGetWaidLitera,
  useOneFormContext,
  formatPrefix,
} from 'src/feature-app';
import { getParamsForm } from 'src/feature-app/get-params-form';
import { CarlineBase, FormDataGroup, FormDataHeaders, FormInfo, Horario, OneFormState, Steps } from 'src/types';
import { ThankYouLeads } from '.';
import { formatFields, formatFlags, formatHeaders, formDataBuilder, getDpsFields } from '../format-fields';
import { getCodInterestCarline, getDescInterestCarline, getMatchData, getSalesGroup } from './helpers';
import { getOfertasData } from './helpers/get-ofertas-data';

export enum LeadGenerico {
  Empezar,
  Mapa,
  Modelo,
  NombreApellido,
  EmailTelefono,
  Contacto,
  Confirmacion,
}

export interface LeadGenericoFormProps {
  steps: Steps[];
  leadData: Partial<FormDataGroup>;
  leadInfo: Partial<FormInfo>;
  firstStep: Steps;
  lastStep: Steps;
  inPage?: boolean;
  isConfigurador?: boolean;
}

export interface LeadsFormsProps {
  isLeadDealer?: boolean;
  configRef?: ConfigurationWithMandatoryBuildabilityV2;
  updatedFormData?: Partial<FormDataGroup>;
  updatedFormInfo?: Partial<FormInfo>;
  isOfertasOrMatch?: boolean;
  inPage?: boolean;
  isConfigurador?: boolean;
}

export const isOfertasForm = (formName: string) => formName === 'lead-oferta';
const getDescFormObjective = (formName: string, carline: CarlineBase, headers: FormDataHeaders) =>
  isOfertasForm(formName) && carline?.campaign?.dfo?.ofertas
    ? carline.campaign.dfo.ofertas
    : headers.DESC_FORMOBJECTIVE;

export function LeadGenericoForm({
  steps,
  leadData,
  leadInfo,
  firstStep,
  lastStep,
  inPage,
  isConfigurador,
}: LeadGenericoFormProps) {
  const { initializeForm } = useOneFormContext();
  const store = useStore();
  const config = useFeatureAppConfig();
  const installations = useGetInstalaciones('VW_DDB_FRM_LEM_REC_LEM'); // Inicialización de las instalaciones
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const faContent = useFaContent();
  const waid = useGetWaidLitera();

  const getHorario = (horario: Horario) => {
    let horarioField = null;

    if (horario) {
      const horarioFormatted = horario === 'morning' ? 'MANANA' : horario === 'evening' ? 'MEDIODIA' : 'TARDE';

      horarioField = { 'fields[CITA_PREVIA_PREFERRED_SCHEDULE]': horarioFormatted };
    }
    return horarioField;
  };

  const getConfiguradorData = async (isConfigurador: boolean) => {
    const { marketingCodeData, marketingCodeResponse } = store.getState().formData.fields;
    if (!marketingCodeResponse) return null;
    const price = await getPriceDataWithMarketingCode(marketingCodeResponse);

    return {
      'fields[COD_VEH_SALESGROUP]': marketingCodeResponse?.consolidatedData?.salesgroupKey,
      'fields[DESC_VEH_SALESGROUP]': marketingCodeResponse?.consolidatedData?.salesgroupName,
      'fields[DESC_TRIM_LINE]': getEquipmentlineName(marketingCodeResponse?.consolidatedData?.modelFilters),
      'fields[COD_VEH_TMAIMG]': marketingCodeResponse?.consolidatedData?.modelKey.split('$')[0],
      'fields[DESC_VEH_TMAIMG]': marketingCodeResponse?.consolidatedData?.modelName,
      'fields[DESC_VEH_MARKETING_CODE]': marketingCodeData?.marketingCode,
      'fields[COD_VEH_COLOR]': marketingCodeResponse?.consolidatedData?.exterior?.code,
      'fields[DESC_VEH_COLOR]': marketingCodeResponse?.consolidatedData?.exterior?.name,
      'fields[COD_VEH_COLOR_INT]': marketingCodeResponse?.consolidatedData?.interior?.code,
      'fields[DESC_VEH_COLOR_INT]': marketingCodeResponse?.consolidatedData?.interior?.name,
      'fields[DESC_VEH_OFFER_PRICE]': price?.body?.amount
        ? `Precio final de la configuración: ${price?.body?.amount}Eur`
        : undefined,
      'fields[COD_INTEREST_PR]': getFeatures(marketingCodeResponse?.consolidatedData?.features),
      ...getWheels(marketingCodeResponse?.expandedConfiguration?.features),
      'fields[COD_INTEREST_CARLINE]': marketingCodeResponse?.consolidatedData?.carlineKey,
      'fields[DESC_INTEREST_CARLINE]': marketingCodeResponse?.consolidatedData?.carlineName,
    };

  };

  const sendForm = (): Promise<boolean> => {
    const { formData, dealersInfo }: OneFormState = store.getState();
    const { fields, headers, flags } = formData;
    const { selectedInstallation, salesGroup, selectedCarline } = dealersInfo;

    const {
      TXT_V_Installation_Data_Name__c,
      TXT_V_KVPS_code__c,
      FRML_TXT_codVentaProvincia__c,
      TXT_V_Installation_Code__c,
    } = selectedInstallation;

    return new Promise<any>(async (resolve, reject) => {
      const { tc, today, referalUrl, url, device } = getParamsForm();
      //resolve(true);
      const data = {
        ...formatHeaders(headers), // Headers comunes a todos los formularios de leads
        ...formatFields(fields.formFields), // Fields comunes a todos los formularios de leads
        ...formatFlags(flags), // Flags comunes a todos los formularios de leads
        'headers[COD_FORM_ID]': `vw-oneform-${fields.formName}`,
        'headers[DESC_URLORIGIN]': url,
        'headers[DESC_DEVICETYPE]': device,
        'headers[DESC_FORMOBJECTIVE]': getDescFormObjective(fields.formName, selectedCarline, headers),
        'headers[DESC_ID_CAMPAIGN]': selectedCarline?.campaign?.salesforce
          ? selectedCarline.campaign.salesforce
          : '7010Y0000001tAOQAY',

        'headers[FECHA_INSERTION_DATE]': today,
        'headers[TC]': tc,

        'fields[DESC_NAME]': fields.name,
        'fields[DESC_SURNAME_1]': fields.surname,
        'fields[DESC_SURNAME_2]': fields.secondSurname,
        'fields[DESC_EMAIL_ADDRESS]': removeWhiteSpace(fields.email),
        'fields[NUM_CELLPHONE]': fields.cellphone ? `${formatPrefix(fields.prefix)}${fields.cellphone}` : undefined,
        'fields[COD_LEAD_ID]': 'VW_DDB_FRM_LEM_REC_LEM',
        'fields[DESC_VEH_BRAND]': 'Volkswagen',
        'fields[DESC_FORM_VERSION]': `vw-oneform-${fields.formName}-${process.env.VERSION}`,
        'fields[DESC_DEALER_CODE_KVPS]': TXT_V_KVPS_code__c,
        'fields[DESC_DEALER_CODE]': TXT_V_Installation_Code__c,
        'fields[DESC_DEALER_PROVINCE]': fields.provinceName,
        'fields[COD_DEALER_PROVINCE]': FRML_TXT_codVentaProvincia__c,
        'fields[DESC_DEALER]': TXT_V_Installation_Data_Name__c,
        'fields[COD_INTEREST_CARLINE]': getCodInterestCarline(dealersInfo, fields.carlineCode),
        'fields[DESC_INTEREST_CARLINE]': getDescInterestCarline(dealersInfo, fields.modelo),
        'fields[DESC_PREFERRED_CONTACT_CHANNEL]': fields.contacto,
        'fields[DESC_LOPD]': 'PERMITE',
        'fields[DESC_TRANF_DATA_THIRD]': 'NO INFORMADO',
        'fields[DESC_LSSI]': fields.lssi ? fields.lssi : 'NO INFORMADO',
        'fields[DESC_LOPD_GDPR_PERFILADO]': fields.tmk,
        'fields[DESC_DATA_ENRICHMENT]': 'NO INFORMADO',
        ...getSalesGroup(salesGroup),
        ...getHorario(fields.horario),
        ...getDpsFields(faContent),
        ...(await getOfertasData(formData, dealersInfo)),
        ...(await getMatchData(formData, dealersInfo, faContent)),
        ...(await getConfiguradorData(isConfigurador)),
      };
      const dataStringified = JSON.stringify(data);
      const dataParsed = JSON.parse(dataStringified);

      const formDataInfo = formDataBuilder(dataParsed);

      const response = await dpsSend(formDataInfo);

      if (response && response.data && response.data.content && response.data.content.code === '200') {
        dispatch({ type: 'SET_FORM_RESPONSE', payload: response.data });
        resolve(true);
      } else {
        resolve(false);
      }
    });
  };

  const leadGenericoData: Partial<FormDataGroup> = {
    headers: {
      DESC_BRAND: 'VW', // OK
      DESC_CAMPAIGNBLOCK: '9. VW LEM', // OK
      DESC_WAID_LITERA: waid ? waid : 'B09C01_LEM_WA1',
      DESC_TOKEN: 'VW_DDB_FRM_LEM_REC_LEM', // OK
      DESC_VN_VO: 'VN', // OK
      DESC_GDPR_ADAPTED: true, // OK
      ...leadData.headers,
    },
    flags: {
      FLAG_MAILRECEPCIONLEAD: 'true',
      FLAG_ISQUALIFIED: 'false',
      REQUIERE_CONTACTO_DEALER: 'false',
    },
    fields: { ...leadData.fields },
  };

  const leadGenericoInfo: Partial<FormInfo> = {
    formStarted: true,
    fullScreen: firstStep.fullScreen ? true : false,
    lastStep,
    sendFormCallback: sendForm,
    numberOfScreens: steps.length,
    screenIndex: firstStep.screenIndex,
    step: firstStep,
    steps,
    stepsHistory: [steps[0].screenIndex],
    thankYouComponent: <ThankYouLeads inPage={inPage} />,
    notALayer: inPage ? true : false,
    formTheme: inPage ? 'main' : 'inverted',
    formType: inPage ? 'inPage' : 'layer',
    ...leadInfo,
  };

  useEffect(() => {
    initializeForm(leadGenericoInfo, leadGenericoData);
    setLoading(false);
  }, []);

  return loading ? null : <FormController steps={steps} screenType={inPage ? 'full-screen' : 'layer-screen'} />;
}
